/**
 * PURPOSE: Administrar programación de deportes individuales en Winscore.
 * RESPONSIBILITIES: Crear, editar, eliminar y publicar actividades individuales por día sin reutilizar el modal colectivo.
 * COLLABORATORS: ModalProgramacionIndividual, API legacy /getmatches, /postmatches, /patchprogramacionmatch, /patchmatchadvancedsettings y /patchprogramacionpublished.
 * ROUTE: winscore/src/area/ProgramacionIndividualArea/ProgramacionIndividualArea.jsx
 */
// src/area/ProgramacionIndividualArea/ProgramacionIndividualArea.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/config.js";
import ModalProgramacionIndividual from "../../components/Modals/ModalProgramacionIndividual/ModalProgramacionIndividual";

import { Tab } from "@headlessui/react";
import toast from "react-hot-toast";
import "./ProgramacionIndividual.css";
import moment from "moment";

import { useParams } from "react-router-dom";

const isByeMatch = (match) => {
  return (
    match?.flag_bye === true ||
    match?.flag_bye === 1 ||
    match?.flag_bye === "1" ||
    String(match?.flag_bye).toLowerCase() === "true"
  );
};

const isDeletedMatch = (match) => Number(match?.state) === -2;

const getMatchDateValue = (match) => {
  if (!match?.date_match) return "";
  return moment(match.date_match).isValid() ? moment(match.date_match).format("YYYY-MM-DD") : "";
};

const getMatchTimeValue = (match) => {
  if (!match?.time_match) return "";
  return moment(match.time_match, ["HH:mm:ss", "HH:mm"], true).isValid()
    ? moment(match.time_match, ["HH:mm:ss", "HH:mm"], true).format("HH:mm")
    : String(match.time_match).slice(0, 5);
};

const buildProgramacionMatchPayload = ({ match, date, time }) => ({
  idmatch: match.idmatch,
  date: date || null,
  time: time || null,
  uniforme1: match.uniforme1 || "",
  uniforme2: match.uniforme2 || "",
  uniformeArquero1: match.uniforme_exclusivo1 || "",
  uniformeArquero2: match.uniforme_exclusivo2 || "",
  uniformeMedias1: match.uniforme_medias1 || "",
  uniformeMedias2: match.uniforme_medias2 || "",
  idsede: match.idsede || match.sede?.idsede || match.Sede?.idsede || null,
  idparticipant1: match.idparticipant1 || "",
  idparticipant2: match.idparticipant2 || "",
  tag_final: match.tag_final || null,
});

const ProgramacionIndividualArea = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState("1");
  const [nroFechasGrupo, setNroFechasGrupo] = useState(0);
  const [nroEtapasFinal, setNroEtapasFinal] = useState(0);
  const [moodName, setMoodName] = useState(1);
  const [useBandera, setUseBandera] = useState(false);
  const [isHavePrevias, setIsHavePrevias] = useState(false);

  const [fechas, setFechas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [matches, setMatches] = useState([]);
  const [sport, setSport] = useState(null);

  const { idevent, idsport } = useParams();

  const [open, setOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [publishedValue, setPublishedValue] = useState("0");
  const [savingPublished, setSavingPublished] = useState(false);
  const [savingActivity, setSavingActivity] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [loading, setLoading] = useState(false);

  const visibleMatches = useMemo(() => {
    return matches.filter((match) => !isByeMatch(match) && !isDeletedMatch(match));
  }, [matches]);

  const selectedDayDate = useMemo(() => {
    const matchWithDate = visibleMatches.find((match) => Boolean(match.date_match));
    return getMatchDateValue(matchWithDate);
  }, [visibleMatches]);

  const getMatches = async (idtypephase = 1, nro_fecha = selectedDate) => {
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/getmatches`, {
        idevent,
        idsport,
        nro_fecha,
        idtypephase,
      });

      const fetchedMatches = (Array.isArray(response.data) ? response.data : [])
        .filter((match) => !isDeletedMatch(match))
        .sort((a, b) => {
          const timeA = a.time_match ? moment(a.time_match, ["HH:mm:ss", "HH:mm"], true) : null;
          const timeB = b.time_match ? moment(b.time_match, ["HH:mm:ss", "HH:mm"], true) : null;

          const validA = timeA && timeA.isValid();
          const validB = timeB && timeB.isValid();

          if (!validA && !validB) return Number(a.nro_match || 0) - Number(b.nro_match || 0);
          if (!validA) return 1;
          if (!validB) return -1;
          return timeA.valueOf() - timeB.valueOf();
        });

      setMatches(fetchedMatches);
    } catch (error) {
      console.error("Error al obtener los matches individuales:", error);
      toast.error("No se pudo cargar la programación individual");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getConfigCategory = async () => {
      const response = await axios.post(`${API_BASE_URL}/config_category`, {
        idevent,
        idsport,
      });

      const data = response.data;
      const fechasGrupo = Number(data.nro_fechas_grupo || 0);
      const etapasFinal = Number(data.nro_etapas_final || 0);

      setNroFechasGrupo(fechasGrupo);
      setNroEtapasFinal(etapasFinal);
      setMoodName(data.moodName);
      setUseBandera(data.useBandera);
      setIsHavePrevias(data.isHavePrevias);
      setFechas(Array.from({ length: fechasGrupo }, (_, i) => String(i + 1)));
      setEtapas(["Octavos", "Cuartos", "Semis", "Final"].slice(4 - etapasFinal));
    };

    const getSport = async () => {
      const sportResponse = await axios.get(`${API_BASE_URL}/sport/?idsport=${idsport}`);
      setSport(sportResponse.data);
    };

    if (idevent && idsport) {
      getConfigCategory();
      getSport();
      getMatches(1, 1);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    const idtypephase = selectedIndex === 0 ? 1 : 2;
    getMatches(idtypephase, selectedDate);
  }, [selectedIndex, selectedDate]);

  useEffect(() => {
    if (visibleMatches.length > 0) {
      const currentPublished = visibleMatches[0]?.published ?? 0;
      setPublishedValue(String(Number(currentPublished)));
    } else {
      setPublishedValue("0");
    }
  }, [visibleMatches, selectedDate]);

  const handleSavePublished = async () => {
    try {
      setSavingPublished(true);
      await axios.patch(`${API_BASE_URL}/patchprogramacionpublished`, {
        idevent: Number(idevent),
        idsport: Number(idsport),
        idtypephase: 1,
        nro_fecha: Number(selectedDate),
        published: Number(publishedValue),
      });

      toast.success(Number(publishedValue) === 1 ? "Día publicado como Oficial" : "Día guardado como Borrador");
      await getMatches(1, selectedDate);
    } catch (error) {
      console.error("Error al actualizar publicación individual", error);
      toast.error("No se pudo actualizar el estado de publicación");
    } finally {
      setSavingPublished(false);
    }
  };

  const syncDayDate = async ({ date, selectedMatchId, selectedTime }) => {
    if (!date) return;

    const updates = visibleMatches.map((match) => {
      const nextTime = Number(match.idmatch) === Number(selectedMatchId) ? selectedTime : getMatchTimeValue(match);
      return axios.patch(`${API_BASE_URL}/patchprogramacionmatch`, buildProgramacionMatchPayload({ match, date, time: nextTime }));
    });

    await Promise.all(updates);
  };

  const handleCreateActivity = async ({ title, time, date }) => {
    const normalizedTitle = String(title || "").trim();
    if (!normalizedTitle) {
      toast.error("Ingresa un título para la actividad");
      return;
    }

    setSavingActivity(true);

    try {
      if (date && visibleMatches.length > 0) {
        await syncDayDate({ date, selectedMatchId: null, selectedTime: null });
      }

      await axios.post(`${API_BASE_URL}/postmatches`, [
        {
          idevent: Number(idevent),
          idsport: Number(idsport),
          idtypephase: 1,
          idparticipant1: null,
          idparticipant2: null,
          nro_fecha: Number(selectedDate),
          date_match: date || null,
          time_match: time || null,
          tag_final: normalizedTitle,
          state: 1,
          published: Number(publishedValue) || 0,
        },
      ]);

      toast.success("Actividad guardada exitosamente");
      setOpen(false);
      setSelectedMatch(null);
      await getMatches(1, selectedDate);
    } catch (error) {
      console.error("Error al guardar actividad individual", error);
      toast.error("No se pudo guardar la actividad");
    } finally {
      setSavingActivity(false);
    }
  };

  const handleUpdateActivity = async ({ title, time, date }) => {
    if (!selectedMatch?.idmatch) return;

    const normalizedTitle = String(title || "").trim();
    if (!normalizedTitle) {
      toast.error("Ingresa un título para la actividad");
      return;
    }

    setSavingActivity(true);

    try {
      await axios.patch(`${API_BASE_URL}/patchmatchadvancedsettings`, {
        idmatch: selectedMatch.idmatch,
        tag_final: normalizedTitle,
      });

      await axios.patch(
        `${API_BASE_URL}/patchprogramacionmatch`,
        buildProgramacionMatchPayload({ match: selectedMatch, date, time })
      );

      if (date) {
        await syncDayDate({ date, selectedMatchId: selectedMatch.idmatch, selectedTime: time });
      }

      toast.success("Actividad actualizada");
      setOpen(false);
      setSelectedMatch(null);
      await getMatches(1, selectedDate);
    } catch (error) {
      console.error("Error al actualizar actividad individual", error);
      toast.error("No se pudo actualizar la actividad");
    } finally {
      setSavingActivity(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!selectedMatch?.idmatch) return;

    const ok = window.confirm("¿Eliminar esta actividad individual?");
    if (!ok) return;

    setDeletingActivity(true);

    try {
      await axios.patch(`${API_BASE_URL}/patchmatchadvancedsettings`, {
        idmatch: selectedMatch.idmatch,
        state: -2,
      });

      toast.success("Actividad eliminada");
      setOpen(false);
      setSelectedMatch(null);
      await getMatches(1, selectedDate);
    } catch (error) {
      console.error("Error al eliminar actividad individual", error);
      toast.error("No se pudo eliminar la actividad");
    } finally {
      setDeletingActivity(false);
    }
  };

  const openCreateModal = () => {
    setSelectedMatch(null);
    setOpen(true);
  };

  const openEditModal = (match) => {
    setSelectedMatch(match);
    setOpen(true);
  };

  const getDayLabel = (fecha) => `Día ${fecha}`;

  return (
    <>
      <div className="container_programacion_individual mx-auto p-4 font-quickSand">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex justify-center space-x-1 rounded-lg bg-gray-100 p-1">
            {nroFechasGrupo > 0 && (
              <Tab
                className={({ selected }) =>
                  selected
                    ? "tab_selected w-full py-2.5 text-sm font-medium leading-5"
                    : "tab_unselected w-full py-2.5 text-sm font-medium leading-5"
                }
              >
                Información
              </Tab>
            )}
          </Tab.List>

          <Tab.Panels className="mt-2">
            {nroFechasGrupo > 0 && (
              <Tab.Panel>
                <div className="title-container mb-4">
                  <h4 className="title text-center text-3xl font-semibold text-gray-700">DÍAS</h4>
                </div>

                <div className="flex justify-center mb-5 flex-wrap gap-2">
                  {fechas.map((fecha) => (
                    <button
                      key={fecha}
                      className={`fechas_button ${selectedDate === fecha ? "selected_date" : "unselected_date"}`}
                      onClick={() => setSelectedDate(fecha)}
                    >
                      <span className="tag_fecha">{getDayLabel(fecha)}</span>
                    </button>
                  ))}
                </div>

                <div className="p-4 bg-white rounded-lg shadow-md mb-5">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estado publicación del día</label>
                      <select
                        value={publishedValue}
                        onChange={(event) => setPublishedValue(event.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="0">Borrador</option>
                        <option value="1">Oficial</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={handleSavePublished}
                      disabled={savingPublished || visibleMatches.length === 0}
                      className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {savingPublished ? "Guardando..." : "Guardar estado"}
                    </button>

                    <span className={`inline-flex justify-center rounded-full px-3 py-2 text-xs font-bold uppercase ${Number(publishedValue) === 1 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {Number(publishedValue) === 1 ? "Oficial" : "Borrador"}
                    </span>
                  </div>
                </div>

                <div className="mb-5 flex justify-end">
                  <button
                    type="button"
                    onClick={openCreateModal}
                    className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold shadow-md"
                  >
                    + Agregar actividad
                  </button>
                </div>
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>

        {loading ? (
          <div className="rounded-lg bg-white p-6 text-center text-lg font-semibold text-gray-600 shadow-md">Cargando actividades...</div>
        ) : visibleMatches.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500 shadow-sm">
            No hay actividades para este día. Agrega la primera actividad para publicarla en Hayllis 2.0.
          </div>
        ) : (
          <div className="cards_container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleMatches.map((match) => (
              <div
                key={match.idmatch}
                className="card bg-white p-6 rounded-lg shadow-lg cursor-pointer flex flex-col items-center transition hover:-translate-y-1 hover:shadow-xl"
                onClick={() => openEditModal(match)}
              >
                <div className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-1">{getDayLabel(selectedDate)}</div>
                <div className="text-xl font-bold text-center mb-2 text-gray-800">{match.tag_final || "Actividad"}</div>
                <div className="text-2xl font-semibold mb-2 text-indigo-700">
                  {match.time_match ? moment(match.time_match, ["HH:mm:ss", "HH:mm"], true).format("h:mm A") : "Hora"}
                </div>
                {match.date_match ? (
                  <div className="text-sm font-semibold text-gray-500">{moment(match.date_match).format("DD/MM/YYYY")}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {open ? (
        <ModalProgramacionIndividual
          open={open}
          setOpen={setOpen}
          match={selectedMatch}
          dayLabel={getDayLabel(selectedDate)}
          defaultDate={selectedMatch ? getMatchDateValue(selectedMatch) || selectedDayDate : selectedDayDate}
          onSave={selectedMatch ? handleUpdateActivity : handleCreateActivity}
          onDelete={selectedMatch ? handleDeleteActivity : undefined}
          saving={savingActivity}
          deleting={deletingActivity}
        />
      ) : null}
    </>
  );
};

export default ProgramacionIndividualArea;
